/**
 * PropertyTemplateList Component
 * 
 * Displays a list of global property templates with search and filters.
 * Used in the Global Properties Management page Templates tab.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Input,
  message,
  Empty,
  Tooltip
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  CopyOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { Modal } from 'antd';
import { PropertyLevelService } from '../../services/api/propertyLevelService';
import { GlobalPropertyTemplate, TemplateFilters } from '../../types/propertyLevel';
import styles from './PropertyTemplateList.module.css';

const { Title, Text } = Typography;
const { Search } = Input;

interface PropertyTemplateListProps {
  onSelectTemplate?: (template: GlobalPropertyTemplate) => void;
  onCreateTemplate?: () => void;
  selectedTemplateId?: string | null;
  compact?: boolean;
}

export const PropertyTemplateList: React.FC<PropertyTemplateListProps> = ({
  onSelectTemplate,
  onCreateTemplate,
  selectedTemplateId,
  compact = false
}) => {
  const [templates, setTemplates] = useState<GlobalPropertyTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filters, setFilters] = useState<TemplateFilters>({});

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await PropertyLevelService.getTemplates(filters);
      setTemplates(response.items);
    } catch (error: any) {
      console.error('Error fetching templates:', error);
      void message.error('Failed to load property templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    void fetchTemplates();
  }, [fetchTemplates]);

  // Filter templates by search text
  const filteredTemplates = useMemo(() => {
    if (!searchText) return templates;
    const lowerSearch = searchText.toLowerCase();
    return templates.filter(
      (template) =>
        template.template_key.toLowerCase().includes(lowerSearch) ||
        template.template_name.toLowerCase().includes(lowerSearch) ||
        template.description?.toLowerCase().includes(lowerSearch) ||
        template.artifact_type_hint?.toLowerCase().includes(lowerSearch)
    );
  }, [templates, searchText]);

  // Handle template deletion
  const handleDeleteTemplate = async (template: GlobalPropertyTemplate) => {
    Modal.confirm({
      title: 'Delete Template',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>Are you sure you want to delete the template "{template.template_name}"?</p>
          <p><Text type="secondary">This action cannot be undone.</Text></p>
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          // Use template.id (UUID) instead of template.template_key
          await PropertyLevelService.deleteTemplate(template.id);
          void message.success('Template deleted successfully');
          await fetchTemplates();
        } catch (error: any) {
          void message.error('Failed to delete template');
        }
      }
    });
  };

  // Handle template copy
  const handleCopyTemplate = async (template: GlobalPropertyTemplate) => {
    try {
      const newTemplate = await PropertyLevelService.createTemplate({
        template_key: `${template.template_key}_copy`,
        template_name: `${template.template_name} (Copy)`,
        description: template.description,
        artifact_type_hint: template.artifact_type_hint,
        metadata: template.metadata
      });
      void message.success('Template copied successfully');
      await fetchTemplates();
      if (onSelectTemplate) {
        onSelectTemplate(newTemplate);
      }
    } catch (error: any) {
      void message.error('Failed to copy template');
    }
  };

  // Table columns
  const columns: ColumnsType<GlobalPropertyTemplate> = [
    {
      title: 'Template Key',
      dataIndex: 'template_key',
      key: 'template_key',
      width: compact ? 150 : 200,
      render: (text: string, record: GlobalPropertyTemplate) => (
        <Space direction="vertical" size={0}>
          <strong>{text}</strong>
          {!compact && record.artifact_type_hint && (
            <Tag color="blue" style={{ marginTop: 4 }}>{record.artifact_type_hint}</Tag>
          )}
        </Space>
      ),
      sorter: (a, b) => a.template_key.localeCompare(b.template_key)
    },
    {
      title: 'Name',
      dataIndex: 'template_name',
      key: 'template_name',
      ellipsis: true,
      sorter: (a, b) => a.template_name.localeCompare(b.template_name)
    },
    ...(compact ? [] : [{
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      width: 250,
      render: (text: string) => (
        <Text type="secondary" ellipsis={{ tooltip: text }}>
          {text || '-'}
        </Text>
      )
    }]),
    {
      title: 'Properties',
      key: 'properties_count',
      width: 100,
      align: 'center' as const,
      render: (_: any, record: GlobalPropertyTemplate) => (
        <Tag color="purple">{record.properties?.length || 0}</Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: compact ? 80 : 120,
      render: (_: any, record: GlobalPropertyTemplate) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                if (onSelectTemplate) {
                  onSelectTemplate(record);
                }
              }}
            />
          </Tooltip>
          {!compact && (
            <Tooltip title="Copy">
              <Button
                type="text"
                size="small"
                icon={<CopyOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  void handleCopyTemplate(record);
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                void handleDeleteTemplate(record);
              }}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <Card
      title={
        compact ? null : (
          <Space>
            <Title level={4} style={{ margin: 0 }}>Property Templates</Title>
            <Tag>{filteredTemplates.length} templates</Tag>
          </Space>
        )
      }
      extra={
        <Space>
          <Search
            placeholder="Search templates..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: compact ? 150 : 200 }}
            allowClear
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => void fetchTemplates()}
            size={compact ? 'small' : 'middle'}
          >
            {compact ? '' : 'Refresh'}
          </Button>
          {onCreateTemplate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onCreateTemplate}
              size={compact ? 'small' : 'middle'}
            >
              {compact ? '' : 'Create'}
            </Button>
          )}
        </Space>
      }
      className={styles.templateListCard}
      styles={{ body: { padding: compact ? 0 : undefined } }}
    >
      {filteredTemplates.length === 0 && !loading ? (
        <Empty
          description="No property templates found"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          {onCreateTemplate && (
            <Button type="primary" onClick={onCreateTemplate}>
              Create First Template
            </Button>
          )}
        </Empty>
      ) : (
        <Table
          columns={columns}
          dataSource={filteredTemplates}
          loading={loading}
          size="small"
          pagination={{
            pageSize: compact ? 10 : 20,
            showSizeChanger: !compact,
            showTotal: (total) => `Total ${total} templates`,
            position: ['bottomCenter']
          }}
          rowKey="id"
          scroll={{ y: compact ? 300 : 'calc(100vh - 400px)' }}
          onRow={(record) => ({
            onClick: () => {
              if (onSelectTemplate) {
                onSelectTemplate(record);
              }
            },
            style: {
              cursor: 'pointer',
              backgroundColor: selectedTemplateId === record.id ? '#e6f7ff' : 'transparent'
            }
          })}
          rowClassName={(record) =>
            selectedTemplateId === record.id ? styles.selectedRow : ''
          }
        />
      )}
    </Card>
  );
};

export default PropertyTemplateList;
